import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHub } from "../../context/HubContext";
import {
  getHubSummary,
  getHubEventBreakdown,
} from "../../services/statisticsService";
import { formatCurrency, formatDate } from "../../utils/formatters";
import StatCard from "../../components/common/StatCard";
import PageHeader from "../../components/common/PageHeader";
import Badge from "../../components/common/Badge";
import {
  Ticket,
  Package,
  TrendingUp,
  Calendar,
  ArrowRight,
  Users,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const statusConfig = {
  0: { label: "Upcoming", variant: "info" },
  1: { label: "Live", variant: "success" },
  2: { label: "Past", variant: "default" },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-bg-elevated border border-border rounded-xl px-4 py-3 text-xs space-y-1">
      <p className="font-semibold text-text-primary mb-2">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
};

const Overview = () => {
  const { hub, loading: hubLoading } = useHub();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState({ chartData: [], events: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hub?.id) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const [s, b] = await Promise.all([
          getHubSummary(hub.id),
          getHubEventBreakdown(hub.id),
        ]);
        setSummary(s);
        setBreakdown(b);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [hub?.id]);

  if (hubLoading || loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-bg-surface rounded-xl w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-bg-surface border border-border rounded-2xl h-24"
            />
          ))}
        </div>
        <div className="bg-bg-surface border border-border rounded-2xl h-64" />
      </div>
    );

  if (!hub)
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-text-muted text-sm">
          No hub found. Create one in the Ingroove app.
        </p>
      </div>
    );

  const stats = [
    {
      icon: <Ticket size={16} className="text-primary" />,
      label: "Tickets Sold",
      value: summary?.tickets?.totalSold?.toLocaleString() ?? "0",
      sub: `of ${summary?.tickets?.totalCapacity?.toLocaleString() ?? "0"} capacity`,
      accent: "bg-primary/10",
    },
    {
      icon: <Package size={16} className="text-secondary" />,
      label: "Extras Sold",
      value: summary?.extras?.totalSold?.toLocaleString() ?? "0",
      sub: `of ${summary?.extras?.totalCapacity?.toLocaleString() ?? "0"} capacity`,
      accent: "bg-secondary/10",
    },
    {
      icon: <TrendingUp size={16} className="text-tertiary" />,
      label: "Revenue Earned",
      value: formatCurrency(summary?.totalEarnedRevenue ?? 0),
      sub: `of ${formatCurrency(summary?.totalPotentialRevenue ?? 0)} potential`,
      accent: "bg-tertiary/10",
    },
    {
      icon: <Calendar size={16} className="text-primary" />,
      label: "Active Events",
      value: summary?.totalEvents?.toLocaleString() ?? "0",
      sub: "Upcoming & live",
      accent: "bg-primary/10",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <div>
        <h2 className="text-2xl font-semibold text-text-primary">
          {getGreeting()},{" "}
          <span className="ingroove-gradient-text">{hub.name} 👋</span>
        </h2>
        <p className="text-text-muted text-sm mt-1">
          Here's what's happening with your hub today.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      {/* Chart + events grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Revenue chart */}
        <div className="lg:col-span-3 bg-bg-surface border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold text-text-primary">
              Revenue by Event
            </h3>
          </div>
          {breakdown.chartData.length === 0 ? (
            <div className="flex items-center justify-center h-48">
              <p className="text-xs text-text-muted">No revenue data yet</p>
            </div>
          ) : (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={breakdown.chartData} barSize={12}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#232E38" />
                  <XAxis
                    dataKey="eventName"
                    tick={{ fill: "#7B8794", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) =>
                      v.length > 12 ? v.slice(0, 12) + "…" : v
                    }
                  />
                  <YAxis
                    tick={{ fill: "#7B8794", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `R${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(255,255,255,0.03)" }}
                  />
                  <Bar
                    dataKey="ticketRevenue"
                    name="Tickets"
                    fill="#139679"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="extrasRevenue"
                    name="Extras"
                    fill="#FCA311"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Upcoming events */}
        <div className="lg:col-span-2 bg-bg-surface border border-border rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">
                Events
              </h3>
            </div>
            <button
              onClick={() => navigate("/events")}
              className="flex items-center gap-1 text-xs text-primary hover:opacity-80 transition"
            >
              View all <ArrowRight size={12} />
            </button>
          </div>

          {breakdown.events.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-xs text-text-muted">No events yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {breakdown.events.slice(0, 5).map((event) => {
                const status = statusConfig[event.status] ?? statusConfig[0];
                const progress =
                  event.tickets?.totalCapacity > 0
                    ? Math.min(
                        (event.tickets.totalSold /
                          event.tickets.totalCapacity) *
                          100,
                        100,
                      )
                    : 0;

                return (
                  <div
                    key={event.eventId}
                    className="px-5 py-3 hover:bg-bg-elevated transition"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm text-text-primary font-medium truncate max-w-[150px]">
                        {event.title}
                      </p>
                      <Badge label={status.label} variant={status.variant} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-text-muted mb-2">
                      <span>{formatDate(event.date)}</span>
                      <span className="flex items-center gap-1">
                        <Users size={10} />
                        {event.tickets?.totalSold ?? 0}/
                        {event.tickets?.totalCapacity ?? 0}
                      </span>
                    </div>
                    <div className="w-full bg-bg-elevated rounded-full h-1">
                      <div
                        className="h-1 rounded-full bg-primary transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Overview;
